import type { PlanningCycle, Todo } from "../../domain/entities.ts";
import { cycleRolloverView } from "./cycle-rollover-view.ts";
import { settingsScopeNote } from "../../ui/settings-feedback.ts";
import { cycleHistoryView, cycleOverviewView } from "./cycle-management-view.ts";

export interface PlanningCycleCapabilities {
  activeRoot(): string | null;
  editable(): boolean;
  cycles(): PlanningCycle[];
  todos(): Todo[];
  actorId: string;
  today(): string;
  formatDate(value: string): string;
  addDays(value: string, days: number): string;
  randomId(): string;
  icon(name: string): string;
  escapeHtml(value: string): string;
  summary(): string;
  create(id: string, rootId: string, actorId: string, value: Omit<PlanningCycle, "id" | "rootId" | "createdAt" | "updatedAt">): Promise<void>;
  changeCycle(id: string, rootId: string, actorId: string, update: (cycle: PlanningCycle) => void): Promise<void>;
  changeTask(id: string, rootId: string, actorId: string, update: (todo: Todo) => void): Promise<void>;
  activate(id: string): void;
  markLocal(): void;
  sync(): Promise<void>;
  render(): Promise<void>;
  toast(message: string, error?: boolean): void;
}

export interface PlanningCycleDraft {
  name: string;
  cadence: PlanningCycle["cadence"];
  startDate: string;
  endDate: string;
  capacity: number | null;
  status: "active";
  deleted: false;
}

export function readPlanningCycleDraft(data: FormData, addDays: PlanningCycleCapabilities["addDays"]): PlanningCycleDraft {
  const cadence = data.get("cadence") as PlanningCycle["cadence"];
  const startDate = String(data.get("startDate"));
  const capacity = String(data.get("capacity") || "") ? Number(data.get("capacity")) : null;
  return {
    name: String(data.get("name") || "").trim(),
    cadence,
    startDate,
    endDate: addDays(startDate, cadence === "weekly" ? 6 : 13),
    capacity,
    status: "active",
    deleted: false,
  };
}

export function nextPlanningCycle(current: PlanningCycle, id: string, formatDate: (value: string) => string, addDays: PlanningCycleCapabilities["addDays"]): PlanningCycleDraft & { id: string } {
  const startDate = addDays(current.endDate, 1);
  return {
    id,
    name: `Cycle ${formatDate(startDate)}`,
    cadence: current.cadence,
    startDate,
    endDate: addDays(startDate, current.cadence === "weekly" ? 6 : 13),
    capacity: current.capacity || null,
    status: "active",
    deleted: false,
  };
}

export function createPlanningCycleController(capabilities: PlanningCycleCapabilities) {
  const closeAndRollover = async (): Promise<void> => {
    const rootId = capabilities.activeRoot();
    const current = capabilities.cycles().find(cycle => cycle.rootId === rootId && !cycle.deleted && cycle.status === "active");
    if (!current || !rootId || !capabilities.editable()) return;
    if (document.querySelector(".cycle-rollover-dialog")) return;
    const incomplete = capabilities.todos().filter(todo => todo.rootId === rootId && !todo.deleted && !todo.archivedAt && !todo.completed && todo.cycleId === current.id);
    const next = nextPlanningCycle(current, capabilities.randomId(), capabilities.formatDate, capabilities.addDays);
    const dialog = document.createElement("dialog");
    dialog.className = "name-dialog cycle-dialog cycle-rollover-dialog";
    dialog.setAttribute("aria-label", "Finish planning cycle");
    dialog.innerHTML = cycleRolloverView(current, next, incomplete, capabilities.icon, capabilities.formatDate);
    let busy = false, created = false, finished = false;
    let selected: string[] | null = null;
    const moved = new Set<string>();
    const checkboxes = [...dialog.querySelectorAll<HTMLInputElement>('[name="carryTask"]')];
    const updateCount = () => { const count = checkboxes.filter(input => input.checked).length; dialog.querySelector<HTMLElement>("[data-cycle-selection-summary]")!.textContent = `${count} unfinished ${count === 1 ? "task" : "tasks"} selected for the next cycle`; };
    checkboxes.forEach(input => input.addEventListener("change", updateCount));
    dialog.querySelectorAll<HTMLElement>("[data-cycle-selection]").forEach(button => button.addEventListener("click", () => { checkboxes.forEach(input => { input.checked = button.dataset.cycleSelection === "all"; }); updateCount(); }));
    dialog.querySelectorAll("[data-rollover-close]").forEach(button => button.addEventListener("click", () => { if (!busy) dialog.close(); }));
    dialog.addEventListener("cancel", event => { if (busy) event.preventDefault(); });
    dialog.querySelector("form")!.addEventListener("submit", async event => {
      event.preventDefault(); if (busy) return;
      if (capabilities.activeRoot() !== rootId || !capabilities.editable()) { capabilities.toast("Cycle editing access changed. Reopen this cycle to continue.", true); return; }
      const fresh = capabilities.cycles().find(cycle => cycle.id === current.id && !cycle.deleted && cycle.status === "active");
      if (!fresh) { capabilities.toast("This cycle is no longer active. Reopen planning cycles.", true); return; }
      busy = true;
      dialog.querySelectorAll<HTMLButtonElement>("button").forEach(button => { button.disabled = true; });
      dialog.querySelector<HTMLFieldSetElement>("fieldset")!.disabled = true;
      const error = dialog.querySelector<HTMLElement>(".review-error")!; error.hidden = true;
      selected ||= checkboxes.filter(input => input.checked).map(input => input.value);
      try {
        if (!created) { await capabilities.create(next.id, rootId, capabilities.actorId, next); created = true; capabilities.markLocal(); }
        for (const id of selected) {
          const todo = capabilities.todos().find(task => task.id === id && !task.deleted && !task.archivedAt && !task.completed && task.cycleId === current.id && task.rootId === rootId);
          if (!todo || moved.has(id)) continue;
          await capabilities.changeTask(todo.id, rootId, capabilities.actorId, draft => { if (!draft.completed && draft.cycleId === current.id) draft.cycleId = next.id; });
          moved.add(id); capabilities.markLocal();
        }
        await capabilities.changeCycle(current.id, rootId, capabilities.actorId, cycle => { cycle.status = "completed"; });
        capabilities.activate(next.id); capabilities.markLocal(); finished = true;
        dialog.close(); await capabilities.render();
        capabilities.toast(`${moved.size} tasks carried forward. Unselected tasks stayed in the closed cycle.`); void capabilities.sync();
      } catch {
        error.textContent = created ? "Some changes have already been saved. Retry to finish this rollover without creating another cycle." : "Could not start the next cycle. Please try again.";
        error.hidden = false;
      } finally {
        busy = false; dialog.querySelectorAll<HTMLButtonElement>("button").forEach(button => { button.disabled = false; });
        dialog.querySelectorAll<HTMLButtonElement>("[data-cycle-selection]").forEach(button => { button.disabled = created; });
        dialog.querySelector<HTMLFieldSetElement>("fieldset")!.disabled = created;
        if (!created) selected = null;
      }
    });
    dialog.addEventListener("close", () => { dialog.remove(); if (created && !finished) void capabilities.render(); });
    updateCount(); document.body.append(dialog); dialog.showModal();
  };

  const open = async (): Promise<void> => {
    const rootId = capabilities.activeRoot();
    if (!rootId || !capabilities.editable()) { capabilities.toast("Cycle management requires edit access", true); return; }
    const cycles = capabilities.cycles().filter(cycle => cycle.rootId === rootId && !cycle.deleted);
    const todos = capabilities.todos();
    const current = cycles.find(cycle => cycle.status === "active");
    const dialog = document.createElement("dialog"); dialog.className = "name-dialog cycle-dialog";
    dialog.innerHTML = `<div class="share-dialog__header"><div><p class="eyebrow">Planning cycles</p><h2>${current ? capabilities.escapeHtml(current.name) : "Start a cycle"}</h2><p>${current ? `${capabilities.formatDate(current.startDate)} – ${capabilities.formatDate(current.endDate)} · ${current.cadence[0].toUpperCase()}${current.cadence.slice(1)}` : "Create a focused weekly or biweekly planning window."}</p></div><button class="icon-button" data-cycle-close aria-label="Close">${capabilities.icon("close")}</button></div>${current ? `<div class="cycle-dialog-body">${cycleOverviewView(current, todos)}<button class="button button--primary" data-cycle-rollover>Finish cycle…</button></div>` : `<form class="cycle-form"><label class="field-label">Cycle name<input name="name" value="Cycle ${capabilities.formatDate(capabilities.today())}" maxlength="120" required></label><div class="field-grid"><label class="field-label">Cadence<select name="cadence"><option value="weekly">Weekly</option><option value="biweekly">Biweekly</option></select></label><label class="field-label">Starts<input name="startDate" type="date" value="${capabilities.today()}" required></label></div><label class="field-label">Task capacity<input name="capacity" type="number" min="1" max="10000" placeholder="Optional planned task limit"></label><div class="dialog-actions"><button class="button button--soft" type="button" data-cycle-close>Cancel</button><button class="button button--primary" type="submit">Start cycle</button></div></form>`}${cycleHistoryView(cycles, todos, capabilities.formatDate)}`;
    dialog.querySelector(".share-dialog__header")!.insertAdjacentHTML("afterend", settingsScopeNote("Shared space", "Cycle changes affect collaborators, not your private My Day list."));
    dialog.setAttribute("aria-label", "Planning cycles");
    dialog.querySelector(".share-dialog__header [data-cycle-close]")!.setAttribute("aria-label", "Close planning cycles");
    const form = dialog.querySelector<HTMLFormElement>(".cycle-form");
    let starting = false;
    if (form) {
      form.insertAdjacentHTML("afterbegin", '<p class="field-note">Nothing changes until you choose Start cycle. Tasks are not automatically added or given new dates.</p>');
      const capacity = form.querySelector<HTMLInputElement>('[name="capacity"]')!;
      capacity.setAttribute("aria-describedby", "cycle-capacity-hint");
      capacity.insertAdjacentHTML("afterend", '<small id="cycle-capacity-hint">Optional. A planning guide, not a limit on adding tasks.</small>');
      form.insertAdjacentHTML("beforeend", '<p class="review-error" role="alert" hidden></p>');
    }
    dialog.addEventListener("cancel", event => { if (starting) event.preventDefault(); });
    dialog.querySelectorAll("[data-cycle-close]").forEach(button => button.addEventListener("click", () => { if (!starting) dialog.close(); }));
    dialog.querySelector("[data-cycle-rollover]")?.addEventListener("click", async () => { dialog.close(); await closeAndRollover(); });
    form?.addEventListener("submit", async event => {
      event.preventDefault();
      if (starting) return;
      const error = form.querySelector<HTMLElement>(".review-error")!;
      if (capabilities.activeRoot() !== rootId || !capabilities.editable()) { error.textContent = "Starting a cycle requires edit access to this space. Reopen planning cycles to continue."; error.hidden = false; return; }
      if (capabilities.cycles().some(cycle => cycle.status === "active" && !cycle.deleted)) { error.textContent = "A cycle has already started. Reopen planning cycles to view it."; error.hidden = false; return; }
      starting = true; error.hidden = true;
      const draft = readPlanningCycleDraft(new FormData(form), capabilities.addDays);
      dialog.querySelectorAll<HTMLButtonElement>("button").forEach(button => { button.disabled = true; });
      const id = capabilities.randomId();
      try {
        await capabilities.create(id, rootId, capabilities.actorId, draft);
      } catch { error.textContent = "Could not start the cycle. Your entries are still here; please try again."; error.hidden = false; return; }
      finally { starting = false; dialog.querySelectorAll<HTMLButtonElement>("button").forEach(button => { button.disabled = false; }); }
      capabilities.activate(id); capabilities.markLocal(); dialog.close();
      try { await capabilities.render(); capabilities.toast("Planning cycle started"); }
      catch { capabilities.toast("Planning cycle saved. Refresh the view to see it.", true); }
      void capabilities.sync();
    });
    document.body.append(dialog); dialog.addEventListener("close", () => dialog.remove()); dialog.showModal();
  };

  return { open, closeAndRollover };
}
